import React, { useEffect, useRef, useState } from "react";

const MAX_MB = 10;

/**
 * Circular image picker with preview. `file` is the newly chosen File (or null);
 * `currentSrc` is an already-saved image shown until a new file is picked.
 */
function ImageUploader({ file, onChange, currentSrc, disabled, allowClear = true }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [drag, setDrag] = useState(false);
  const [currentBroken, setCurrentBroken] = useState(false);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const accept = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) return setError("Please choose an image file.");
    if (f.size > MAX_MB * 1024 * 1024) return setError(`Image must be under ${MAX_MB} MB.`);
    setError("");
    onChange(f);
  };

  const shown = preview || (!currentBroken ? currentSrc : null);

  return (
    <div>
      <div className='bh-uploader'>
        <button
          type='button'
          className={`bh-uploader__preview ${drag ? "is-drag" : ""}`}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            if (!disabled) accept(e.dataTransfer.files?.[0]);
          }}
          disabled={disabled}
          aria-label='Choose conversation image'
        >
          {shown ? (
            <img src={shown} alt='' onError={() => !preview && setCurrentBroken(true)} />
          ) : (
            <i className='fa fa-camera' aria-hidden='true'></i>
          )}
        </button>
        <div>
          <div className='bh-uploader__actions'>
            <button
              type='button'
              className='bh-btn bh-btn--ghost bh-btn--sm'
              onClick={() => inputRef.current?.click()}
              disabled={disabled}
            >
              {shown ? "Change image" : "Upload image"}
            </button>
            {allowClear && file && (
              <button
                type='button'
                className='bh-btn bh-btn--text-danger bh-btn--sm'
                onClick={() => {
                  setError("");
                  onChange(null);
                }}
                disabled={disabled}
              >
                Remove
              </button>
            )}
          </div>
          <p className={`bh-hint ${error ? "bh-hint--error" : ""}`}>{error || `JPG or PNG, up to ${MAX_MB} MB`}</p>
        </div>
        <input
          ref={inputRef}
          type='file'
          accept='image/*'
          className='bh-uploader__input'
          onChange={(e) => {
            accept(e.target.files?.[0]);
            e.target.value = ""; // allow re-picking the same file
          }}
        />
      </div>
    </div>
  );
}

export default ImageUploader;
